import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PrintTimetable() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    const isTeacher = user.role === "teacher";
    api
      .get(isTeacher ? "/teacher/timetable/export" : "/school/timetable/export", {
        params: {
          format: "json",
          class_id: params.get("class_id") || undefined,
          teacher_ids: params.get("teacher_ids") || undefined,
          room_id: params.get("room_id") || undefined,
        },
      })
      .then((r) => {
        setData(r.data);
        setTimeout(() => window.print(), 500);
      })
      .catch((e) => setError(formatErr(e, "Could not load the timetable")));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return <div className="p-10 text-center font-semibold text-red-500" data-testid="print-error">{error}</div>;
  }
  if (!data) {
    return <div className="p-10 text-center text-slate-400">Preparing timetable…</div>;
  }

  return (
    <div className="min-h-screen bg-white p-8 print:p-2" data-testid="print-timetable">
      <div className="flex items-end justify-between border-b-2 border-slate-800 pb-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold">Weekly Timetable</h1>
          <p className="text-sm text-slate-500 mt-1">{data.school_name} · {data.filter_label}</p>
        </div>
        <p className="text-sm font-semibold">Monday – Friday</p>
      </div>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="border border-slate-300 bg-slate-100 px-3 py-2 text-left text-xs uppercase tracking-wide w-16">Period</th>
            {data.days.map((d) => (
              <th key={d} className="border border-slate-300 bg-slate-100 px-3 py-2 text-left text-xs uppercase tracking-wide">{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) =>
            r.break ? (
              <tr key={i}>
                <td colSpan={data.days.length + 1} className="border border-slate-300 bg-amber-50 px-3 py-1 text-center text-[10px] font-extrabold uppercase tracking-widest text-amber-700" data-testid={`print-break-${i}`}>
                  {r.break}
                </td>
              </tr>
            ) : (
              <tr key={i}>
                <td className="border border-slate-300 bg-slate-50 px-3 py-2 font-bold text-xs">{r.label}</td>
                {r.cells.map((c, j) => (
                  <td key={j} className="border border-slate-300 px-3 py-2 align-top" data-testid={`print-cell-${j}-${i}`}>
                    {(c ? c.split("\n") : []).map((line, k) =>
                      k % 2 === 0 ? (
                        <p key={k} className={`text-xs font-bold ${k > 1 ? "mt-1.5" : ""}`}>{line}</p>
                      ) : (
                        <p key={k} className="text-[10px] text-slate-500">{line}</p>
                      )
                    )}
                  </td>
                ))}
              </tr>
            )
          )}
        </tbody>
      </table>
      <p className="text-xs text-slate-400 mt-4">Generated by EDUJAC</p>
    </div>
  );
}
